import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionHistoryProps,
  ActionHistoryItem,
  ActionHistoryAction,
  SalaryDetailsData,
} from "../types";
import { COLORS } from "../constants";
import EditSalaryInput from "./EditSalaryInput";
import DatePicker, { DateObject } from "react-multi-date-picker";
import UploadField from "../../../components/UploadField/UploadField";
import { uploadDocument } from "../hook/API";
import { safeDateObject } from "../hook/ScreenHelper";

interface ExtendedActionHistoryProps extends ActionHistoryProps {
  processData?: ActionHistoryItem;
  actions?: ActionHistoryAction[];
  salaryDetails?: SalaryDetailsData;
  save?: (data: any) => void;
  financialDataId?: number;
  salaryType?: string;
}

/* ===================== CONSTANTS ===================== */

const EMPLOYMENT_SECTORS = Object.freeze([
  { id: 1, name: "Private", code: "P" },
  { id: 3, name: "Retired", code: "R" },
  { id: 4, name: "Government-Military", code: "GM" },
  { id: 5, name: "Government-Non-Military", code: "GNM" },
  // { id: 6, name: "Unemployed", code: "UE" },
]);

const ACTIV_STATUS = Object.freeze([
  { id: 1, name: "Active", code: "ACTIVE" },
  { id: 3, name: "In Active", code: "INACTIVE" },
]);

const fields = Object.freeze([
  { key: "employerName", label: "Employer Name" },
  { key: "sector", label: "Employment Sector" },
  { key: "bank", label: "Bank" },
  { key: "basicSalary", label: "Basic Salary" },
  { key: "housingAllowance", label: "Housing" },
  { key: "otherAllowance", label: "Other Allowance" },
  { key: "dateOfJoining", label: "Date of Joining" },
  { key: "payMonth", label: "Pay month" },
  { key: "salaryStatus", label: "Status" },
]);

const NUMBER_ONLY_FIELDS = [
  "basicSalary",
  "housingAllowance",
  "otherAllowance",
];

const MAX_SALARY_AMOUNT = 500000;

const isValidAmount = (key: string, value: string): boolean => {
  if (!/^\d+$/.test(value)) return false;
  const amount = Number(value);
  const min = key === "basicSalary" ? 1 : 0;
  return amount >= min && amount <= MAX_SALARY_AMOUNT;
};

/* ===================== HELPER FUNCTIONS ===================== */

// Parse payMonth from YYYYMM format (e.g., "202505" -> DateObject for May 2025)
const parsePayMonth = (value?: string | null): DateObject | null => {
  if (!value) return null;

  try {
    // Check if value is in YYYYMM format (6 digits)
    if (typeof value === "string" && /^\d{6}$/.test(value)) {
      const year = parseInt(value.substring(0, 4), 10);
      const month = parseInt(value.substring(4, 6), 10);
      
      if (year > 0 && month >= 1 && month <= 12) {
        // Create date string in YYYY-MM-01 format for DateObject parsing
        const dateString = `${year}-${String(month).padStart(2, "0")}-01`;
        const date = new DateObject(dateString);
        if (date.isValid) {
          return date;
        }
      }
    }
    
    // Fallback to default date parsing
    return safeDateObject(value);
  } catch {
    return null;
  }
};

/* ===================== TYPES ===================== */

type SalaryFormState = {
  employerName: string;
  sector: string;
  bank: string;
  housingAllowance: string;
  otherAllowance: string;
  dateOfJoining: DateObject | null;
  payMonth: DateObject | null;
  salaryStatus: string;
  basicSalary: string;
};

type DocumentType = "salaryDoc" | "bankDoc" | "gosiDoc";

/* ===================== COMPONENT ===================== */

const EditSalaryPopup: React.FC<ExtendedActionHistoryProps> = ({
  close,
  save,
  salaryDetails,
  financialDataId,
  salaryType
}) => {
  const [showSectorDropdown, setShowSectorDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [salaryData, setSalaryData] = useState<SalaryFormState>({
    employerName: salaryDetails?.employerName || "",
    sector: salaryDetails?.sector || "",
    bank: salaryDetails?.bank || "",
    housingAllowance: String(salaryDetails?.housingAllowance ?? ""),
    otherAllowance: String(salaryDetails?.otherAllowance ?? ""),
    dateOfJoining: safeDateObject(salaryDetails?.dateOfJoining),
    payMonth: parsePayMonth(salaryDetails?.payMonth),
    salaryStatus: salaryDetails?.salaryStatus || "",
    basicSalary: String(salaryDetails?.basicSalary ?? "")
  });
  const [documents, setDocuments] = useState<any>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});

  /* ===================== MEMOIZED VALUES ===================== */
  useEffect(() => {
    if (salaryType === "MASDR" && !salaryData.sector) {
      setSalaryData((prev) => ({
        ...prev,
        sector: "P",
      }));
    }
  }, [salaryType]);
  const isFormValid = useMemo(() => {
    return Object.entries(salaryData).every(([key, value]) => {
      if (!value) return false;

      if (typeof value === "string") {
        // Trim string values to treat space-only strings as empty
        const trimmedValue = value.trim();
        if (!trimmedValue) return false;

        if (NUMBER_ONLY_FIELDS.includes(key)) {
          return isValidAmount(key, trimmedValue);
        }
      }

      if (key === "dateOfJoining" || key === "payMonth") {
        return value !== null;
      }

      return true;
    });
  }, [salaryData]);

  // Check if a specific field is invalid
  const isFieldInvalid = useCallback((key: string, value: any): boolean => {
    if (!value) return true;

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      if (!trimmedValue) return true;

      if (NUMBER_ONLY_FIELDS.includes(key)) {
        return !isValidAmount(key, trimmedValue);
      }
    }

    if (key === "dateOfJoining" || key === "payMonth") {
      return value === null;
    }

    return false;
  }, []);

  /* ===================== HANDLERS ===================== */

  const toggleSectorDropdown = useCallback(() => {
    setShowSectorDropdown((p) => !p);
    setShowStatusDropdown(false);
  }, []);

  const toggleStatusDropdown = useCallback(() => {
    setShowStatusDropdown((p) => !p);
    setShowSectorDropdown(false);
  }, []);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  const handleDocumentUpload = async (
    file: File | undefined,
    docType: DocumentType,
    name: string
  ) => {
    if (!file) {
      setDocuments((prev) => ({
        ...prev,
        [docType]: undefined,
        [`${docType}Url`]: undefined,
      }));
      return;
    }

    // const fileName = file.name || name;
    try {
      setUploading((p) => ({ ...p, [docType]: true }));
      const docUrl = await uploadDocument(file, docType, financialDataId);
      if (docUrl?.data?.documentUrl) {
        setDocuments((prev) => ({
          ...prev,
          [docType]: file,
          [`${docType}Url`]: docUrl?.data?.documentUrl,
        }));
      }
    } finally {
      setUploading((p) => ({ ...p, [docType]: false }));
    }
  };

  const handleSave = useCallback(() => {
    const payload = {
      ...salaryData,
      dateOfJoining: salaryData.dateOfJoining?.format("YYYY-MM-DD"),
      payMonth: salaryData.payMonth?.format("YYYYMM"),
      salaryDocUrl: documents?.salaryDocUrl || salaryDetails?.salaryCertificateUrl,
      bankDocUrl: documents?.bankDocUrl || salaryDetails?.bankStatementUrl,
      gosiDocUrl: documents?.gosiDocUrl || salaryDetails?.gosiCertificateUrl,
    };

    save(payload);
    close();
  }, [salaryData, close]);

  const pickerStyle = {
    background: "transparent",
    border: "none",
    boxShadow: "none",
    cursor: "pointer",
  };

  /* ===================== RENDER ===================== */

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
      style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
    >
      <div
        className="bg-white p-3 rounded shadow"
        style={{
          width: "90%",
          maxWidth: "900px",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center">
          <h5 style={{ color: COLORS.TEXT_PRIMARY }}>Details</h5>
          <button className="btn p-2" onClick={close}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* FORM */}
        <div className="row mx-3">
          {fields.map((f) => {
            if (f.key === "dateOfJoining") {
              const isInvalid = isFieldInvalid("dateOfJoining", salaryData.dateOfJoining);
              return (
                <div key={f.key} className="col-md-3 mb-4">
                  <label className="form-label">{f.label}</label>
                  <div
                    style={{
                      background: "#f8f8f8",
                      borderRadius: "14px",
                      padding: "10px",
                      border: isInvalid ? "1px solid #d92d20" : "none",
                    }}
                  >
                    <DatePicker
                      editable={false}
                      value={salaryData.dateOfJoining}
                      onChange={(d) =>
                        !Array.isArray(d) &&
                        setSalaryData((p) => ({ ...p, dateOfJoining: d }))
                      }
                      format="YYYY-MM-DD"
                      maxDate={new Date()}
                      inputClass="form-control"
                      style={pickerStyle}
                    />
                  </div>
                </div>
              );
            }

            if (f.key === "payMonth") {
              const isInvalid = isFieldInvalid("payMonth", salaryData.payMonth);
              return (
                <div key={f.key} className="col-md-3 mb-4">
                  <label className="form-label">{f.label}</label>
                  <div
                    style={{
                      background: "#f8f8f8",
                      borderRadius: "14px",
                      padding: "10px",
                      border: isInvalid ? "1px solid #d92d20" : "none",
                    }}
                  >
                    <DatePicker
                      editable={false}
                      onlyMonthPicker
                      value={salaryData.payMonth}
                      onChange={(d) =>
                        !Array.isArray(d) &&
                        setSalaryData((p) => ({ ...p, payMonth: d }))
                      }
                      format="MMM YYYY"
                      maxDate={new Date()}
                      inputClass="form-control"
                      style={pickerStyle}
                    />
                  </div>
                </div>
              );
            }

            /* EMPLOYMENT SECTOR */
            if (f.key === "sector") {
              const isInvalid = isFieldInvalid("sector", salaryData.sector);
              const isMasdar = salaryType === "MASDR";
              return (
                <div key={f.key} className="col-md-3 mb-4 position-relative">
                  <label className="form-label">{f.label}</label>
                  <div
                    style={{
                      background: "#f8f8f8",
                      borderRadius: "14px",
                      padding: "10px",
                      border: isInvalid ? "1px solid #d92d20" : "none",
                    }}
                  >
                    <div
                      className="form-control"
                      style={{
                        background: "transparent",
                        border: "none",
                        display: "flex",
                        justifyContent: "space-between",
                        cursor: isMasdar ? "default" : "pointer",
                      }}
                      onClick={!isMasdar ? toggleSectorDropdown : undefined}
                    >
                      <span>
                        {isMasdar ? "Private" : salaryData.sector || "Select"}
                      </span>
                      {!isMasdar && <i className="bi bi-chevron-down" />}
                    </div>
                  </div>

                  {!isMasdar && showSectorDropdown && (
                    <div className="position-absolute bg-white shadow rounded mt-1 w-100">
                      {EMPLOYMENT_SECTORS.map((sector) => (
                        <div
                          key={sector.id}
                          style={{ padding: "10px", cursor: "pointer" }}
                          onClick={() => {
                            setSalaryData((prev) => ({
                              ...prev,
                              sector: sector.code,
                            }));
                            setShowSectorDropdown(false);
                          }}
                        >
                          {sector.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            /* STATUS */
            if (f.key === "salaryStatus") {
              const isInvalid = isFieldInvalid("salaryStatus", salaryData.salaryStatus);
              return (
                <div key={f.key} className="col-md-3 mb-4 position-relative">
                  <label className="form-label">{f.label}</label>
                  <div
                    style={{
                      background: "#f8f8f8",
                      borderRadius: "14px",
                      padding: "10px",
                      border: isInvalid ? "1px solid #d92d20" : "none",
                    }}
                  >
                    <div
                      className="form-control"
                      style={{
                        background: "transparent",
                        border: "none",
                        display: "flex",
                        justifyContent: "space-between",
                        cursor: "pointer",
                      }}
                      onClick={toggleStatusDropdown}
                    >
                      <span>{salaryData?.salaryStatus || "Select"}</span>
                      <i className="bi bi-chevron-down" />
                    </div>
                  </div>

                  {showStatusDropdown && (
                    <div className="position-absolute bg-white shadow rounded mt-1 w-100">
                      {ACTIV_STATUS.map((item) => (
                        <div
                          key={item.id}
                          style={{ padding: "10px", cursor: "pointer" }}
                          onClick={() => {
                            setSalaryData((prev) => ({
                              ...prev,
                              salaryStatus: item.code,
                            }));
                            setShowStatusDropdown(false);
                          }}
                        >
                          {item.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            /* DEFAULT STRING INPUTS */
            const value = salaryData[f.key as keyof SalaryFormState];
            if (typeof value !== "string") return null;

            return (
              <EditSalaryInput
                key={f.key}
                label={f.label}
                value={value}
                valueColor={COLORS.TEXT_PRIMARY}
                isInvalid={isFieldInvalid(f.key, value)}
                onChange={(val) => {
                  if (NUMBER_ONLY_FIELDS.includes(f.key) && !/^\d*$/.test(val))
                    return;

                  setSalaryData((prev) => ({
                    ...prev,
                    [f.key]: val,
                  }));
                }}
              />
            );
          })}
        </div>
        <small className="text-muted d-block mt-2">
          Amounts must be whole numbers up to {MAX_SALARY_AMOUNT.toLocaleString("en-US")} SAR.
          Basic Salary must be at least 1.
        </small>

        {/* ADDITIONAL DOCUMENTS */}
        <div className="mt-4">
          <h5 style={{ color: COLORS.TEXT_PRIMARY }}>Additional Documents</h5>

          <div className="d-flex gap-3 flex-wrap mt-3">
            <UploadField
              placeholder="Salary Certificate"
              file={documents.salaryDoc}
              loading={uploading.salaryDoc}
              onChange={(file) =>
                handleDocumentUpload(file, "salaryDoc", "Salary_Certificate")
              }
            />

            <UploadField
              placeholder="Bank Statement"
              file={documents.bankDoc}
              loading={uploading.bankDoc}
              onChange={(file) =>
                handleDocumentUpload(file, "bankDoc", "Bank_Statement")
              }
            />

            <UploadField
              placeholder="GOSI Certificate"
              file={documents.gosiDoc}
              loading={uploading.gosiDoc}
              onChange={(file) =>
                handleDocumentUpload(file, "gosiDoc", "GOSI_Certificate")
              }
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="d-flex justify-content-end gap-3 mt-5">
          <button
            type="button"
            className="btn d-flex align-items-center px-5 py-2"
            style={{
              borderWidth: 1,
              borderColor: COLORS.ELECTRIC_NAVY,
              borderStyle: "solid",
              color: COLORS.ELECTRIC_NAVY,
              fontWeight: 700,
              fontSize: "15px",
            }}
            onClick={handleClose}
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={!isFormValid}
            className="btn d-flex align-items-center px-5 py-2"
            style={{
              backgroundColor: COLORS.ELECTRIC_NAVY,
              color: "white",
              fontWeight: 700,
              fontSize: "15px",
              borderWidth: 1,
              borderColor: COLORS.ELECTRIC_NAVY,
              borderStyle: "solid",
              opacity: isFormValid ? 1 : 0.5,
              cursor: isFormValid ? "pointer" : "not-allowed",
            }}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(EditSalaryPopup);
